import { ChangeEvent, FC } from "react";
import { FormikHelpers, useFormik } from "formik";
import { DateTime } from "luxon";
import * as Yup from "yup";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { transformToFormattedDate } from "~/OnboardingApp/utils";
import { optionalDate, requiredString } from "~/utils/validations";
import { MilestoneSelectInput } from "./components";

type Values = {
  upcomingMilestone: string;
  upcomingMilestoneDate: string;
};

const validationSchema = Yup.object({
  upcomingMilestone: requiredString,
  upcomingMilestoneDate: optionalDate,
});

export const NextMilestonePage: FC = () => {
  const { onSubmit } = useSubmitOnboardingQuestion();
  const { upcomingMilestone, upcomingMilestoneDate, journeyLooksLike } =
    useOnboarding();
  // This step is necessary because this used to be multi-select and is stored as an Array on the backend.
  // We convert the string value submitted on this page to an Array before persisting in the Onboarding::Updater
  const selectedJourney = journeyLooksLike?.[0] || "";

  const initialUpcomingMilestoneDate = upcomingMilestoneDate
    ? DateTime.fromISO(upcomingMilestoneDate).toFormat("MM / dd / yyyy")
    : "";

  const initialValues: Values = {
    upcomingMilestone: upcomingMilestone || "",
    upcomingMilestoneDate: initialUpcomingMilestoneDate || "",
  };

  const getOptionsForJourney = (journey: string) => {
    const commonOptions = [
      { label: "Two week wait", value: "two_week_wait" },
      { label: "Pregnancy test", value: "pregnancy_test" },
      { label: "I don't know yet", value: "unknown" },
    ];

    switch (journey) {
      case "Timed intercourse/at home insemination":
        return [
          {
            label: "Using an ovulation predictor kit",
            value: "using_ovulation_predictor_kit",
          },
          { label: "Awaiting LH/ovulation surge", value: "awaiting_lh_surge" },
          ...commonOptions,
        ];
      case "Trying IUI":
        return [
          { label: "Taking meds for IUI", value: "taking_meds_iui" },
          { label: "Monitoring", value: "monitoring" },
          { label: "IUI procedure", value: "iui_procedure" },
          ...commonOptions,
        ];
      case "Trying IVF":
        return [
          { label: "Awaiting protocol", value: "awaiting_protocol" },
          { label: "IVF priming", value: "ivf_priming" },
          { label: "Ordering medications", value: "ordering_medications" },
          {
            label: "First night of medications",
            value: "first_night_medications",
          },
          { label: "IVF retrieval", value: "ivf_retrieval" },
          { label: "Embryo grading", value: "embryo_grading" },
          { label: "Embryo transfer", value: "embryo_transfer" },
          ...commonOptions,
        ];
      case "Pursuing surrogacy":
        return [
          {
            label: "Creating embryos for a future surrogate",
            value: "creating_embryos_surrogate",
          },
          { label: "Looking for a surrogate", value: "looking_for_surrogate" },
          { label: "Screening a surrogate", value: "screening_surrogate" },
          {
            label: "In an active surrogacy cycle",
            value: "active_surrogacy_cycle",
          },
          { label: "I don't know yet", value: "unknown" },
        ];
      case "Pursuing egg donor":
        return [
          { label: "Looking for a donor", value: "looking_for_donor" },
          { label: "Selected a donor", value: "selected_donor" },
          {
            label: "Actively in a cycle using donor egg",
            value: "active_donor_egg_cycle",
          },
          { label: "I don't know yet", value: "unknown" },
        ];
      case "Pursuing sperm donor":
        return [
          { label: "Looking for a donor", value: "looking_for_donor" },
          { label: "Selected a donor", value: "selected_donor" },
          {
            label: "Actively in a cycle using donor sperm",
            value: "active_donor_sperm_cycle",
          },
          { label: "I don't know yet", value: "unknown" },
        ];
      case "Pursuing fertility preservation":
        return [
          { label: "Receiving protocol", value: "receiving_protocol" },
          { label: "Ordering medications", value: "ordering_medications" },
          {
            label: "First night of medications",
            value: "first_night_medications",
          },
          { label: "Retrieval", value: "retrieval" },
          { label: "I don't know yet", value: "unknown" },
        ];
      default:
        return [{ label: "I don't know yet", value: "unknown" }];
    }
  };

  const options = getOptionsForJourney(selectedJourney);

  const handleSubmit = (
    values: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    const { upcomingMilestone, upcomingMilestoneDate } = values;
    const date = DateTime.fromFormat(upcomingMilestoneDate, "MM / dd / yyyy");
    const formattedDate = (date.isValid ? date.toISO() : "") as string;

    onSubmit(
      { upcomingMilestone, upcomingMilestoneDate: formattedDate },
      formikHelpers,
    );
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
  });

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const oldValue = formik.values.upcomingMilestoneDate;

    const formattedDate = transformToFormattedDate({ value, oldValue });

    formik.setFieldValue("upcomingMilestoneDate", formattedDate, true);
  };

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            description="Select the option that best describes your next milestone in your fertility journey."
            question="What's your next milestone?"
          />
          <MilestoneSelectInput name="upcomingMilestone" options={options} />

          {formik.values.upcomingMilestone &&
            formik.values.upcomingMilestone !== "unknown" && (
              <QuestionContainer>
                <StringInput
                  id="upcomingMilestoneDate"
                  onChange={handleDateChange}
                  placeholder="mm / dd / yyyy"
                  label="When do you expect this milestone?"
                />
              </QuestionContainer>
            )}
        </QuestionContainer>
        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
